import ApiService from '@/core/services/ApiService';
import { buildTokenRequestConfig } from '@/core/services/TokenRequestConfig';

export type OAuthTokenPayload = {
  access_token: string;
  token_type: string;
  expires_in?: number;
};

/**
 * Cache in-memory del OAuth2 client-credentials (TFSM).
 * Reutiliza el token vigente según `expires_in` del IdP, deduplica fetches concurrentes
 * e invalida ante 401 de Bearer.
 */
class OAuthTokenManager {
  private static accessToken = '';
  private static tokenType = 'Bearer';
  private static expiresAt = 0;
  private static inflight: Promise<OAuthTokenPayload> | null = null;

  /**
   * Holgura mínima (segundos) por desfase de reloj / latencia de red.
   * La vigencia real viene de `expires_in` (p.ej. 3600); no se usa un TTL fijo de 5 min.
   */
  private static readonly CLOCK_SKEW_SECONDS = 30;

  static invalidate(): void {
    this.accessToken = '';
    this.tokenType = 'Bearer';
    this.expiresAt = 0;
    this.inflight = null;
  }

  static isValid(): boolean {
    return Boolean(this.accessToken) && Date.now() < this.expiresAt;
  }

  /**
   * Token usable para Authorization. Varias llamadas concurrentes comparten un solo POST.
   */
  static async getValidToken(forceRefresh = false): Promise<OAuthTokenPayload> {
    if (!forceRefresh && this.isValid()) {
      return {
        access_token: this.accessToken,
        token_type: this.tokenType,
      };
    }

    if (this.inflight) {
      return this.inflight;
    }

    this.inflight = this.fetchNewToken()
      .then((payload) => {
        this.accessToken = payload.access_token;
        this.tokenType = payload.token_type || 'Bearer';
        const expiresIn =
          typeof payload.expires_in === 'number' && payload.expires_in > 0
            ? payload.expires_in
            : 3600;
        // Usar expires_in del servidor; restar solo skew mínimo (no 5 min fijos)
        const usableSeconds = Math.max(1, expiresIn - this.CLOCK_SKEW_SECONDS);
        this.expiresAt = Date.now() + usableSeconds * 1000;
        return {
          access_token: this.accessToken,
          token_type: this.tokenType,
          expires_in: expiresIn,
        };
      })
      .finally(() => {
        this.inflight = null;
      });

    return this.inflight;
  }

  private static async fetchNewToken(): Promise<OAuthTokenPayload> {
    const { params, headers } = buildTokenRequestConfig();
    const baseUrl = process.env.VUE_APP_API_URL_TOYOTA_BUS_TOKEN;
    if (!baseUrl) {
      throw new Error('URL de autenticación no configurada');
    }

    const oauthPath = `${String(baseUrl).replace(/\/$/, '')}/OAuth2Token`;
    const urlObj = new URL(oauthPath);
    if (urlObj.protocol !== 'https:') {
      throw new Error('Solo se permiten URLs HTTPS para transferencia de información sensible');
    }

    const finalValidatedUrl = ApiService.validateUrlForFetch(urlObj.toString());
    const trustedUrl = ApiService.validateTrustedDestination(finalValidatedUrl, baseUrl);

    const response = await fetch(trustedUrl, {
      method: 'POST',
      body: params,
      headers,
      redirect: 'error',
    });

    if (!response.ok) {
      throw new Error('Error de autenticación');
    }

    const data = await response.json();
    const access_token = data?.access_token;
    if (!access_token || typeof access_token !== 'string') {
      throw new Error('Error de autenticación');
    }

    return {
      access_token,
      token_type: data?.token_type || 'Bearer',
      expires_in: data?.expires_in,
    };
  }
}

export default OAuthTokenManager;
